import React, { useState, useMemo, useCallback } from 'react';
import { Search, ChevronRight, ChevronDown, Package, FlaskConical, Wrench, Settings, Baby, Users, UserPlus, HardHat, CheckSquare, Landmark, Clock, TrendingUp, Brain, Shield, Server, Info, X } from 'lucide-react';
import { REFERENTIAL_SECTIONS, REFERENTIAL_VERSION, REFERENTIAL_DATE } from '../../data/technicalReferential';

// Map icon names to components
const ICON_MAP = {
  Package, FlaskConical, Wrench, Settings, Baby, Users, UserPlus,
  HardHat, CheckSquare, Landmark, Clock, TrendingUp, Brain, Shield, Server
};

// Color map for section badges
const COLOR_MAP = {
  teal: 'bg-teal-100 text-teal-700',
  blue: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
  pink: 'bg-pink-100 text-pink-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  green: 'bg-green-100 text-green-700',
  violet: 'bg-violet-100 text-violet-700',
  rose: 'bg-rose-100 text-rose-700',
  lime: 'bg-lime-100 text-lime-700',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700',
  red: 'bg-red-100 text-red-700',
  slate: 'bg-slate-100 text-slate-700',
};

// Simple markdown renderer (tables, headers, bold, code, lists)
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Table detection
    if (line.includes('|') && i + 1 < lines.length && lines[i + 1]?.match(/^\|[\s-|]+\|$/)) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse table
      const headers = tableLines[0].split('|').map(c => c.trim()).filter(Boolean);
      const rows = tableLines.slice(2).map(row =>
        row.split('|').map(c => c.trim()).filter(Boolean)
      );
      elements.push(
        <div key={key++} className="overflow-x-auto my-3">
          <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-50">
                {headers.map((h, hi) => (
                  <th key={hi} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-b border-gray-200">
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-gray-50/50">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 text-gray-700">
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Headers
    if (line.startsWith('## ')) {
      elements.push(<h2 key={key++} className="text-lg font-bold text-gray-800 mt-6 mb-3 pb-2 border-b border-gray-200">{renderInline(line.slice(3))}</h2>);
      i++; continue;
    }
    if (line.startsWith('### ')) {
      elements.push(<h3 key={key++} className="text-base font-semibold text-gray-700 mt-4 mb-2">{renderInline(line.slice(4))}</h3>);
      i++; continue;
    }

    // List items
    if (line.match(/^- /)) {
      const listItems = [];
      while (i < lines.length && lines[i].match(/^- /)) {
        listItems.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={key++} className="list-disc list-inside space-y-1 my-2 text-gray-700 text-sm">
          {listItems.map((li, idx) => <li key={idx}>{renderInline(li)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (line.match(/^\d+\. /)) {
      const listItems = [];
      while (i < lines.length && lines[i].match(/^\d+\. /)) {
        listItems.push(lines[i].replace(/^\d+\.\s*/, ''));
        i++;
      }
      elements.push(
        <ol key={key++} className="list-decimal list-inside space-y-1 my-2 text-gray-700 text-sm">
          {listItems.map((li, idx) => <li key={idx}>{renderInline(li)}</li>)}
        </ol>
      );
      continue;
    }

    // Regular paragraph
    elements.push(<p key={key++} className="text-gray-700 text-sm my-2 leading-relaxed">{renderInline(line)}</p>);
    i++;
  }

  return elements;
};

// Inline markdown: bold, code, italic
const renderInline = (text) => {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Code blocks
    const codeMatch = remaining.match(/`([^`]+)`/);
    // Bold
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);

    let firstMatch = null;
    let matchType = null;

    if (codeMatch && (!firstMatch || codeMatch.index < firstMatch.index)) {
      firstMatch = codeMatch;
      matchType = 'code';
    }
    if (boldMatch && (!firstMatch || boldMatch.index < firstMatch.index)) {
      firstMatch = boldMatch;
      matchType = 'bold';
    }

    if (!firstMatch) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    // Text before match
    if (firstMatch.index > 0) {
      parts.push(<span key={key++}>{remaining.slice(0, firstMatch.index)}</span>);
    }

    if (matchType === 'code') {
      parts.push(
        <code key={key++} className="px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-mono">
          {firstMatch[1]}
        </code>
      );
    } else if (matchType === 'bold') {
      parts.push(<strong key={key++} className="font-semibold text-gray-800">{firstMatch[1]}</strong>);
    }

    remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
  }

  return parts;
};

const TechnicalReferential = () => {
  const [activeSection, setActiveSection] = useState(REFERENTIAL_SECTIONS[0]?.id);
  const [activeSubsection, setActiveSubsection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(
    Object.fromEntries(REFERENTIAL_SECTIONS.map(s => [s.id, true]))
  );

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  // Search across all content
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return null;
    const q = searchQuery.toLowerCase();
    const results = [];
    REFERENTIAL_SECTIONS.forEach(section => {
      section.subsections.forEach(sub => {
        const content = sub.content.toLowerCase();
        if (content.includes(q) || sub.title.toLowerCase().includes(q)) {
          // Find matching lines for context
          const contentLines = sub.content.split('\n');
          const matchLines = contentLines
            .filter(line => line.toLowerCase().includes(q))
            .slice(0, 3)
            .map(line => line.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim());
          results.push({
            sectionId: section.id,
            sectionTitle: section.title,
            sectionColor: section.color,
            subsectionId: sub.id,
            subsectionTitle: sub.title,
            matchLines
          });
        }
      });
    });
    return results;
  }, [searchQuery]);

  const currentSection = REFERENTIAL_SECTIONS.find(s => s.id === activeSection);
  const currentSubsection = activeSubsection
    ? currentSection?.subsections.find(s => s.id === activeSubsection)
    : null;

  const navigateTo = (sectionId, subsectionId) => {
    setActiveSection(sectionId);
    setActiveSubsection(subsectionId || null);
    setSearchQuery('');
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 flex flex-col h-full -mt-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Info size={20} className="text-primary" />
            Referentiel Technique & Fonctionnel
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Version {REFERENTIAL_VERSION} — {new Date(REFERENTIAL_DATE).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Rechercher dans le referentiel..."
          className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Search results overlay */}
      {searchResults && (
        <div className="mb-4 bg-white border border-gray-200 rounded-lg shadow-sm max-h-[400px] overflow-y-auto">
          {searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">Aucun resultat pour "{searchQuery}"</div>
          ) : (
            <div className="divide-y divide-gray-100">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500">
                {searchResults.length} resultat{searchResults.length > 1 ? 's' : ''}
              </div>
              {searchResults.map((r, idx) => (
                <button
                  key={idx}
                  onClick={() => navigateTo(r.sectionId, r.subsectionId)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${COLOR_MAP[r.sectionColor] || 'bg-gray-100 text-gray-600'}`}>
                      {r.sectionTitle}
                    </span>
                    <ChevronRight size={12} className="text-gray-300" />
                    <span className="text-sm font-medium text-gray-700">{r.subsectionTitle}</span>
                  </div>
                  {r.matchLines.length > 0 && (
                    <div className="text-xs text-gray-400 truncate mt-1">
                      {r.matchLines[0]}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main content: sidebar + content */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Navigation sidebar */}
        <div className="w-56 flex-shrink-0 overflow-y-auto bg-white border border-gray-200 rounded-lg max-h-[calc(100vh-280px)]">
          <nav className="py-2">
            {REFERENTIAL_SECTIONS.map(section => {
              const Icon = ICON_MAP[section.icon] || Package;
              const isActive = activeSection === section.id;
              const isExpanded = expandedSections[section.id];
              return (
                <div key={section.id}>
                  <button
                    onClick={() => {
                      setActiveSection(section.id);
                      setActiveSubsection(null);
                      toggleSection(section.id);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                      isActive && !activeSubsection
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={14} className={isActive ? 'text-primary' : 'text-gray-400'} />
                    <span className="flex-1 text-left truncate">{section.title}</span>
                    {isExpanded
                      ? <ChevronDown size={12} className="text-gray-400" />
                      : <ChevronRight size={12} className="text-gray-400" />
                    }
                  </button>
                  {isExpanded && (
                    <div className="ml-6 border-l border-gray-100">
                      {section.subsections.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => navigateTo(section.id, sub.id)}
                          className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                            activeSubsection === sub.id
                              ? 'text-primary font-semibold bg-primary/5'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {sub.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Content area */}
        <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-y-auto max-h-[calc(100vh-280px)] p-6">
          {activeSubsection && currentSubsection ? (
            <>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 mb-4 text-xs text-gray-400">
                <button onClick={() => setActiveSubsection(null)} className="hover:text-primary transition">
                  {currentSection.title}
                </button>
                <ChevronRight size={10} />
                <span className="text-gray-600 font-medium">{currentSubsection.title}</span>
              </div>
              {/* Content */}
              <div className="prose-sm max-w-none">
                {renderMarkdown(currentSubsection.content)}
              </div>
            </>
          ) : currentSection ? (
            <>
              {/* Section overview */}
              <div className="flex items-center gap-3 mb-6">
                {(() => { const Icon = ICON_MAP[currentSection.icon] || Package; return <Icon size={24} className="text-primary" />; })()}
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{currentSection.title}</h2>
                  <p className="text-sm text-gray-400">{currentSection.subsections.length} sous-sections</p>
                </div>
              </div>
              {/* List subsections as cards */}
              <div className="grid gap-3">
                {currentSection.subsections.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => setActiveSubsection(sub.id)}
                    className="text-left p-4 border border-gray-100 rounded-lg hover:border-primary/30 hover:bg-primary/5 transition group"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-700 group-hover:text-primary transition">{sub.title}</h3>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-primary transition" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                      {sub.content.split('\n').find(l => l.trim() && !l.startsWith('#'))?.replace(/\*\*/g, '').replace(/`/g, '').trim() || ''}
                    </p>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 py-12">Selectionnez une section</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicalReferential;
